import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type {
  Automation,
  AutomationConflict,
  AutomationEnableBlocked,
  AutomationHistory,
  AutomationOptions,
  AutomationPayload,
  AutomationPreview,
  AutomationValidationError
} from "../types/automation.types.ts"

const BASE_URL = "/automations"

const PATHS = {
  list: `${BASE_URL}/`,
  options: `${BASE_URL}/options/`,
  preview: `${BASE_URL}/preview/`,
  detail: (id: string) => `${BASE_URL}/${id}/`,
  enable: (id: string) => `${BASE_URL}/${id}/enable/`,
  disable: (id: string) => `${BASE_URL}/${id}/disable/`,
  duplicate: (id: string) => `${BASE_URL}/${id}/duplicate/`,
  pendingCount: (id: string) => `${BASE_URL}/${id}/pending-count/`,
  history: (id: string) => `${BASE_URL}/${id}/history/`
}

// GETs come back bare. Successful writes are enveloped by the backend's
// EnvelopeJSONRenderer, so their payload sits at `details.data` — read it
// with `unwrapEnvelope`. The 409 conflict and enable-blocked bodies are raw.

export const getAutomationOptions = () =>
  request.api.get<AutomationOptions, BaseApiResponse<void>>(PATHS.options)

export const getAutomations = (params?: {
  page?: number
  page_size?: number
}) =>
  request.api.get<BasePagination<Automation>, BaseApiResponse<void>>(
    PATHS.list,
    params
  )

export const getAutomation = (id: string) =>
  request.api.get<Automation, BaseApiResponse<void>>(PATHS.detail(id))

export const createAutomation = (payload: AutomationPayload) =>
  request.api.post<BaseApiResponse<Automation>, AutomationValidationError>(
    PATHS.list,
    payload
  )

export const updateAutomation = (
  id: string,
  payload: AutomationPayload & { revision: number }
) =>
  request.api.put<
    BaseApiResponse<Automation>,
    AutomationValidationError | AutomationConflict
  >(PATHS.detail(id), payload)

export const enableAutomation = (id: string) =>
  request.api.post<
    BaseApiResponse<Automation>,
    AutomationEnableBlocked | BaseApiResponse<void>
  >(PATHS.enable(id))

export const disableAutomation = (id: string) =>
  request.api.post<BaseApiResponse<Automation>, BaseApiResponse<void>>(
    PATHS.disable(id)
  )

export const duplicateAutomation = (id: string) =>
  request.api.post<BaseApiResponse<Automation>, BaseApiResponse<void>>(
    PATHS.duplicate(id)
  )

export const deleteAutomation = (id: string) =>
  request.api.delete<
    BaseApiResponse<{ cancelled_count: number }>,
    BaseApiResponse<void>
  >(PATHS.detail(id))

export const getPendingCount = (id: string) =>
  request.api.get<
    { pending_count: number; pending_runs?: number },
    BaseApiResponse<void>
  >(PATHS.pendingCount(id))

export const previewAutomation = (
  payload: AutomationPayload & { lead_id?: string | null }
) =>
  request.api.post<
    BaseApiResponse<AutomationPreview>,
    AutomationValidationError
  >(PATHS.preview, payload)

export const getAutomationHistory = (id: string, page = 1) =>
  request.api.get<AutomationHistory, BaseApiResponse<void>>(PATHS.history(id), {
    page
  })
